import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Input, Modal, Space, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import storeApi from '../../apis/store';
import {
  ACTIVE_STORE_CHANGED_EVENT,
  getActiveStoreId,
} from '../../constants/store';
import { ButtonType } from '../../constants/style';
import { IStore } from '../../interfaces/Store';
import { FlexContainer, PageContainer } from '../../styles/Global';
import Button from '../Button/Button';
import PageHeader from '../PageHeader/PageHeader';

const Stores = () => {
  const [stores, setStores] = useState<IStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<IStore | null>(null);
  const [editName, setEditName] = useState('');
  const [activeStoreId, setActiveId] = useState<string | null>(
    getActiveStoreId()
  );

  const activeStore = stores.find((s) => s.id === activeStoreId);
  const canManage = !!activeStore?.isDefault;

  useEffect(() => {
    const onStoreChanged = () => setActiveId(getActiveStoreId());
    window.addEventListener(ACTIVE_STORE_CHANGED_EVENT, onStoreChanged);
    return () =>
      window.removeEventListener(ACTIVE_STORE_CHANGED_EVENT, onStoreChanged);
  }, []);

  const loadStores = useCallback(async () => {
    setLoading(true);
    try {
      const data = await storeApi.listStores();
      setStores(data);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const handleCreate = async () => {
    if (!name.trim()) {
      message.warning('Store name is required');
      return;
    }
    setCreating(true);
    try {
      await storeApi.createStore({ name: name.trim() });
      setName('');
      message.success('Store created');
      await loadStores();
    } catch (e: any) {
      message.error(e?.message || 'Failed to create store');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing || !editName.trim()) {
      return;
    }
    try {
      await storeApi.updateStore(editing.id, { name: editName.trim() });
      message.success('Store updated');
      setEditing(null);
      await loadStores();
    } catch (e: any) {
      message.error(e?.message || 'Failed to update store');
    }
  };

  const handleDelete = (store: IStore) => {
    Modal.confirm({
      title: `Delete store "${store.name}"?`,
      content:
        'Historical orders keep their store attribution. This store will no longer appear in the picker.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await storeApi.deleteStore(store.id);
          message.success('Store deleted');
          await loadStores();
        } catch (e: any) {
          message.error(e?.message || 'Failed to delete store');
        }
      },
    });
  };

  const columns: ColumnsType<IStore> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Default',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault: boolean) =>
        isDefault ? <Tag color="blue">Default</Tag> : null,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, store: IStore) => (
        <Space>
          <Button
            type={ButtonType.OUTLINE}
            disabled={!canManage}
            onClick={() => {
              setEditing(store);
              setEditName(store.name);
            }}
          >
            <EditOutlined /> Rename
          </Button>
          <Button
            type={ButtonType.OUTLINE}
            disabled={!canManage || store.isDefault}
            onClick={() => handleDelete(store)}
          >
            <DeleteOutlined /> Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title="Stores" />
      {!canManage && (
        <p style={{ marginBottom: 16, color: '#666' }}>
          Switch to the default store to create or manage stores for this
          account.
        </p>
      )}
      {canManage && (
        <FlexContainer style={{ gap: 12, marginBottom: 16 }}>
          <Input
            placeholder="New store name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ maxWidth: 280 }}
            onPressEnter={handleCreate}
          />
          <Button
            type={ButtonType.PRIMARY}
            loading={creating}
            onClick={handleCreate}
          >
            <PlusOutlined /> Add store
          </Button>
        </FlexContainer>
      )}
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={stores}
        pagination={false}
      />
      <Modal
        title="Rename store"
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleUpdate}
        okText="Save"
      >
        <Input
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onPressEnter={handleUpdate}
        />
      </Modal>
    </PageContainer>
  );
};

export default Stores;
